/**
 * Viewport truth — ONE source for device class, orientation, and the
 * keyboard offset (mobile UX program, M1).
 *
 * Replaces the scattered checks that predated it:
 *   - `ui.tsx isMobile()` (width-only, ignored pointer type) → `deviceSig`
 *   - `layouts.ts isMobileViewport()` (kept as a thin wrapper over
 *     `isTouchDevice()` — same media query, same semantics)
 *   - `mixins/media-query.ts` (dead, deleted)
 *
 * Device classification mirrors the stylesheet's phone compound
 * (`styles.ts` fullscreen treatments) so JS and CSS can never disagree:
 *
 *   phone   = coarse pointer AND (width ≤ 767px OR height ≤ 500px)
 *   tablet  = coarse pointer, larger than phone
 *   desktop = fine pointer (incl. docked iPads with a trackpad)
 *
 * Signals are LIVE — matchMedia change listeners re-evaluate on rotation,
 * window resize, and foldable posture changes, so `TransitionContext.mobile`
 * reflects reality mid-session instead of freezing at boot.
 *
 * `installViewportAttrs(root)` stamps `data-device` / `data-orientation`
 * on `.sp-root` and keeps them updated; new CSS keys on the attributes
 * instead of repeating media-query compounds.
 */

import { effect, signal } from '@preact/signals';

export type DeviceClass = 'phone' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

// The stylesheet's phone compound, verbatim (styles.ts fullscreen block).
const PHONE_QUERY =
  '(pointer:coarse) and (max-width:767px), (pointer:coarse) and (max-height:500px)';
const COARSE_QUERY = '(pointer: coarse)';
// The historical isMobileViewport() semantics — ANY touch-first device.
const TOUCH_QUERY = '(pointer: coarse), (hover: none)';
const PORTRAIT_QUERY = '(orientation: portrait)';

function mq(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

function classifyDevice(): DeviceClass {
  if (mq(PHONE_QUERY)) return 'phone';
  if (mq(COARSE_QUERY)) return 'tablet';
  return 'desktop';
}

function classifyOrientation(): Orientation {
  return mq(PORTRAIT_QUERY) ? 'portrait' : 'landscape';
}

/** Live device class. SSR/non-browser default: 'desktop'. */
export const deviceSig = signal<DeviceClass>(classifyDevice());
/** Live orientation. SSR default: 'landscape'. */
export const orientationSig = signal<Orientation>(classifyOrientation());
/**
 * Live keyboard offset in px (0 when the keyboard is down). Mirrors the
 * `--sp-kb-offset` CSS var that `mobile-floor.ts` maintains — that module
 * keeps owning the var + viewport meta (shipped, verified); this signal is
 * the JS-side read for surfaces that need the number (keyboard-up budget).
 */
export const kbOffsetSig = signal(0);

/**
 * The historical `isMobileViewport()` semantics — any touch-first device
 * (tablets included). Kept EXACTLY as before so `capLayoutForMobile` and
 * boot behavior don't change in the dark-foundations slice. Phase 2+
 * distinguishes phone vs tablet via `deviceSig`.
 */
export function isTouchDevice(): boolean {
  return mq(TOUCH_QUERY);
}

let installed = false;

/** Re-evaluate all signals (idempotent; safe to call from any listener). */
function refresh(): void {
  const d = classifyDevice();
  if (deviceSig.peek() !== d) deviceSig.value = d;
  const o = classifyOrientation();
  if (orientationSig.peek() !== o) orientationSig.value = o;
  if (typeof window !== 'undefined' && window.visualViewport) {
    const kb = Math.max(
      0,
      Math.round(window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop),
    );
    if (kbOffsetSig.peek() !== kb) kbOffsetSig.value = kb;
  }
}

/** Install the global listeners once (any widget mount). */
function installTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const onChange = (): void => refresh();
  for (const q of [PHONE_QUERY, COARSE_QUERY, PORTRAIT_QUERY]) {
    try {
      const m = window.matchMedia(q);
      // Safari <14 shipped addListener only; both exist in our support matrix
      // but guard anyway (same defensive posture as the rest of the widget).
      if (typeof m.addEventListener === 'function') m.addEventListener('change', onChange);
      else if (typeof m.addListener === 'function') m.addListener(onChange);
    } catch {
      /* matchMedia unavailable — resize fallback below still fires */
    }
  }
  window.addEventListener('resize', onChange, { passive: true });
  window.addEventListener('orientationchange', onChange, { passive: true });
  window.visualViewport?.addEventListener('resize', onChange, { passive: true });
  refresh();
}

/**
 * Stamp + maintain `data-device` / `data-orientation` on a widget root.
 * Returns a disposer (used by tests; production roots live for the page).
 */
export function installViewportAttrs(root: HTMLElement): () => void {
  installTracking();
  return effect(() => {
    root.setAttribute('data-device', deviceSig.value);
    root.setAttribute('data-orientation', orientationSig.value);
  });
}

/**
 * Below this the visual-viewport delta is a toolbar or a rounding, not a
 * keyboard (the shortest soft keyboards are ~200 px; iOS URL-bar collapse
 * is ~50 px).
 */
export const KEYBOARD_UP_PX = 80;

export type KeyboardState = 'up' | 'down';

export function keyboardState(kbOffsetPx: number): KeyboardState {
  return kbOffsetPx >= KEYBOARD_UP_PX ? 'up' : 'down';
}
